"use client";

/**
 * Runtime glue between the web app and the native mobile shell
 * (mobile/, Capacitor). Renders nothing; mounted once in the locale
 * layout and active ONLY when the page runs inside the shell (UA
 * token check via isNativeApp).
 *
 * Responsibilities:
 *  - appUrlOpen: routes URLs the OS hands the app into the WebView.
 *    Two shapes arrive here:
 *      az.menzil.app://auth?code=...&return_to=...  - the OAuth
 *        handoff return (login happened in the system browser; the
 *        one-time code is exchanged for a session cookie INSIDE the
 *        WebView so the cookie lands in the right cookie jar), and
 *      https://menzil.ai/...  - share/permalink deep links.
 *  - pushNotificationActionPerformed: a notification tap carries
 *    data.url (set by the backend FCM sender); navigate to it.
 *  - cold start: getLaunchUrl() covers URLs that launched the app
 *    before listeners attached.
 */
import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

import { API_BASE } from "@/lib/api";
import { closeExternal, isNativeApp, nativePlugins } from "@/lib/native-app";

const AUTH_SCHEME_PREFIX = "az.menzil.app://auth";

/** menzil.ai URL or path -> in-app path. Foreign origins return null. */
function toInAppPath(url: string): string | null {
  if (url.startsWith("/")) return url;
  try {
    const u = new URL(url);
    if (u.hostname === "menzil.ai" || u.hostname.endsWith(".menzil.ai")) {
      return u.pathname + u.search + u.hash;
    }
  } catch {
    /* not a URL */
  }
  return null;
}

export default function AppShellBridge() {
  const router = useRouter();
  // A code must be exchanged exactly once even if appUrlOpen fires
  // together with a cold-start getLaunchUrl for the same URL.
  const handledUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isNativeApp()) return;
    const plugins = nativePlugins();
    if (!plugins) return;

    const handleUrl = async (url: string) => {
      if (!url || handledUrls.current.has(url)) return;
      handledUrls.current.add(url);

      if (url.startsWith(AUTH_SCHEME_PREFIX)) {
        // OAuth handoff return. Exchange the one-time code for a
        // session cookie in THIS WebView context, then hard-navigate
        // so every auth consumer re-reads /auth/me fresh.
        let code = "";
        let returnTo = "/";
        try {
          const u = new URL(url.replace("az.menzil.app://", "https://menzil.ai/"));
          code = u.searchParams.get("code") ?? "";
          returnTo = u.searchParams.get("return_to") ?? "/";
        } catch {
          return;
        }
        if (!returnTo.startsWith("/")) returnTo = "/";
        if (!code) return;
        try {
          const res = await fetch(`${API_BASE}/auth/app-exchange`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ code }),
          });
          await closeExternal();
          if (res.ok) {
            window.location.assign(returnTo);
          }
        } catch {
          /* expired/consumed code - the user can just retry sign-in */
        }
        return;
      }

      const path = toInAppPath(url);
      if (path) router.push(path);
    };

    const handles: Array<{ remove: () => Promise<void> }> = [];

    // The injected bridge returns listener handles SYNCHRONOUSLY (the
    // npm typings say Promise<handle> - that's the bundled-runtime
    // shape). Promise.resolve() tolerates both.
    void Promise.resolve(
      plugins.App?.addListener("appUrlOpen", (data) => {
        void handleUrl(data.url);
      }),
    ).then((h) => { if (h) handles.push(h); });

    void Promise.resolve(
      plugins.PushNotifications?.addListener(
        "pushNotificationActionPerformed",
        (action) => {
          const url = action.notification.data?.url;
          if (url) {
            const path = toInAppPath(url);
            if (path) router.push(path);
          }
        },
      ),
    ).then((h) => { if (h) handles.push(h); });

    void Promise.resolve(plugins.App?.getLaunchUrl()).then((launch) => {
      if (launch?.url) void handleUrl(launch.url);
    });

    return () => {
      for (const h of handles) void h.remove();
    };
  }, [router]);

  return null;
}
